// ============================================================
// VCS.Session — Session-Persistenz, Room-ID, Storage-Migration
// ============================================================
window.VCS = window.VCS || {};

VCS.Session = class {

    // ── Konstanten ───────────────────────────────────────────

    static SESSION_KEY          = 'call-session-v1';
    static ACTIVE_ROOM_ID_KEY   = 'active-room-id';
    static STORAGE_SCHEMA_KEY   = 'call-storage-schema';
    static STORAGE_SCHEMA_VER   = 2;
    static AUDIO_MODE_KEY       = 'audio-mode';
    static AUDIO_MODE_USER_SET  = 'audio-mode-user-set';

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // View-Cache
        this._view = {
            rejoinHint:  $('#rejoinHint'),
            regNameInput:$('#regNameInput'),
            hostWaiting: $('#hostWaiting'),
            guestWaiting:$('#guestWaiting'),
        };
    }

    // ── Room-ID ──────────────────────────────────────────────

    normalizeRoomId(value) {
        return (value || '').trim().toLowerCase().slice(0, 64);
    }

    updateRoomId(newRoomId) {
        VCS.state.ROOM_ID = this.normalizeRoomId(newRoomId);
        if (!VCS.state.ROOM_ID) {
            return;
        }
        this._saveActiveRoomId(VCS.state.ROOM_ID);
    }

    // ── Session speichern / laden ────────────────────────────

    save() {
        localStorage.setItem(VCS.Session.SESSION_KEY, JSON.stringify(VCS.state.sessionState));
    }

    update(patch) {
        VCS.state.sessionState = { ...VCS.state.sessionState, ...patch };
        this.save();
    }

    load() {
        try {
            const raw = localStorage.getItem(VCS.Session.SESSION_KEY);
            if (!raw) {
                return;
            }
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') {
                return;
            }
            VCS.state.sessionState = {
                name : typeof parsed.name === 'string' ? parsed.name : '',
                phase: (parsed.phase === 'waiting' || parsed.phase === 'call') ? parsed.phase : 'prejoin',
                role : (parsed.role  === 'host'    || parsed.role  === 'guest') ? parsed.role  : null,
            };
        } catch (_) {
            VCS.state.sessionState = { name: '', phase: 'prejoin', role: null };
        }
    }

    resetToPreJoin(keepName = true) {
        VCS.state.sessionState = {
            name : keepName ? (VCS.state.myName || VCS.state.sessionState.name || '') : '',
            phase: 'prejoin',
            role : null,
        };
        this.save();
        VCS.state.ROOM_ID = null;
        this._clearActiveRoomId();

        // Waiting-Text zurücksetzen
        this._view.hostWaiting.find('.waiting-title').text(t('waitingForPartner'));
        this._view.hostWaiting.find('.waiting-subtitle').text(t('partnerCanJoin'));
    }

    // ── Rejoin-Hint ──────────────────────────────────────────

    setRejoinHint(visible) {
        this._view.rejoinHint.toggleClass('hidden', !visible);
    }

    // ── Session-UI wiederherstellen (beim Start) ─────────────

    restoreUI() {
        this.load();
        this.setRejoinHint(false);

        const { sessionState } = VCS.state;

        if (sessionState.name) {
            VCS.state.myName = sessionState.name;
            this._view.regNameInput.val(sessionState.name);
        }

        const resumable = sessionState.phase === 'waiting' || sessionState.phase === 'call';

        if (resumable && VCS.state.ROOM_ID) {
            // Aktiver Call mit Room-ID → Rejoin versuchen
            VCS.state.sessionState.phase    = 'waiting';
            this.save();
            VCS.state.pendingRegisterOnOpen = true;
            VCS.nav?.showScreen?.('waitingScreen');
            this._view.hostWaiting.addClass('hidden');
            this._view.guestWaiting.removeClass('hidden');
            this.setRejoinHint(true);
        } else if (VCS.state.chatRegistered) {
            // Kein aktiver Call → Session bereinigen, App Shell zeigen
            if (resumable) {
                this.update({ name: VCS.state.myName, role: null, phase: null });
                this._clearActiveRoomId();
            }
            VCS.nav?.showAppShell?.('tabChats');
        }
    }

    // ── Alle Client-Daten löschen ────────────────────────────

    async clearAllClientData() {
        localStorage.clear();
        sessionStorage.clear();

        // Cookies löschen
        document.cookie.split(';').forEach((c) => {
            const name = c.trim().split('=')[0];
            if (!name) {
                return;
            }
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure`;
        });

        // E2E-Cache + IndexedDB löschen
        if (typeof E2E !== 'undefined') {
            E2E.clearCache();
        }
        try {
            await new Promise((resolve, reject) => {
                const req = indexedDB.deleteDatabase('e2e-keys');
                req.onsuccess = resolve;
                req.onerror   = reject;
                req.onblocked = resolve;
            });
        } catch (_) {}

        // MediaCache IndexedDB löschen
        try {
            VCS.mediaCache?.revokeAll();
            await new Promise((resolve, reject) => {
                const req = indexedDB.deleteDatabase('vcs-media-cache');
                req.onsuccess = resolve;
                req.onerror   = reject;
                req.onblocked = resolve;
            });
        } catch (_) {}

        // Emoji-Picker IndexedDB löschen (de, en, ru)
        for (const locale of ['de', 'en', 'ru']) {
            try {
                await new Promise((resolve, reject) => {
                    const req = indexedDB.deleteDatabase(`emoji-picker-element-${locale}`);
                    req.onsuccess = resolve;
                    req.onerror   = reject;
                    req.onblocked = resolve;
                });
            } catch (_) {}
        }

        // Web-Push-Subscription deaktivieren
        if ('serviceWorker' in navigator) {
            try {
                const reg = await navigator.serviceWorker.ready;
                // SW mitteilen, dass kein Phone mehr aktiv ist
                if (reg.active) {
                    reg.active.postMessage({ type: 'set-phone', phone: null });
                }
                // Push-Subscription entfernen
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    await sub.unsubscribe();
                }
            } catch (_) {}
        }

        // Service-Worker-Caches löschen
        if ('caches' in window) {
            try {
                const names = await caches.keys();
                await Promise.all(names.map(n => caches.delete(n)));
            } catch (_) {}
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        this._runStorageMigrations();
        this._checkDataEpoch();

        // Gespeicherte Room-ID laden
        const savedRoom = localStorage.getItem(VCS.Session.ACTIVE_ROOM_ID_KEY);
        if (savedRoom) {
            VCS.state.ROOM_ID = this.normalizeRoomId(savedRoom);
        }

        // myId persistieren
        localStorage.setItem('call-client-id', VCS.state.myId);
    }

    // ── Private Methoden ─────────────────────────────────────

    _saveActiveRoomId(roomId) {
        const n = this.normalizeRoomId(roomId);
        if (n) {
            localStorage.setItem(VCS.Session.ACTIVE_ROOM_ID_KEY, n);
        }
    }

    _clearActiveRoomId() {
        localStorage.removeItem(VCS.Session.ACTIVE_ROOM_ID_KEY);
    }

    _normalizeAudioMode(value) {
        if (value === 'standard' || value === 'speech' || value === 'music') {
            return value;
        }
        return null;
    }

    _runStorageMigrations() {
        let schema = Number.parseInt(localStorage.getItem(VCS.Session.STORAGE_SCHEMA_KEY) || '0', 10);
        if (!Number.isFinite(schema) || schema < 0) {
            schema = 0;
        }

        if (schema < 1) {
            // Default für Audio wurde auf "music" angehoben — alten impliziten "standard"-Wert löschen
            const mode    = this._normalizeAudioMode(localStorage.getItem(VCS.Session.AUDIO_MODE_KEY));
            const userSet = localStorage.getItem(VCS.Session.AUDIO_MODE_USER_SET) === '1';
            if (mode === 'standard' && !userSet) {
                localStorage.removeItem(VCS.Session.AUDIO_MODE_KEY);
            }
            schema = 1;
        }

        if (schema < 2) {
            // Default für Audio wurde auf "speech" geändert — alten impliziten "music"-Wert löschen
            const mode2    = this._normalizeAudioMode(localStorage.getItem(VCS.Session.AUDIO_MODE_KEY));
            const userSet2 = localStorage.getItem(VCS.Session.AUDIO_MODE_USER_SET) === '1';
            if (mode2 === 'music' && !userSet2) {
                localStorage.removeItem(VCS.Session.AUDIO_MODE_KEY);
            }
            schema = 2;
        }

        localStorage.setItem(VCS.Session.STORAGE_SCHEMA_KEY, String(Math.max(schema, VCS.Session.STORAGE_SCHEMA_VER)));
    }

    _checkDataEpoch() {
        if (typeof DATA_EPOCH === 'undefined') {
            return;
        }
        const stored = localStorage.getItem('data-epoch');
        if (stored === DATA_EPOCH) {
            return;
        }
        // Epoch fehlt oder stimmt nicht überein — alte Daten löschen
        const hadRegistration = !!localStorage.getItem('chat-phone');
        localStorage.clear();
        sessionStorage.clear();
        try {
            indexedDB.deleteDatabase('e2e-keys');
        } catch (_) {}
        localStorage.setItem('data-epoch', DATA_EPOCH);
        if (hadRegistration) {
            location.reload();
        }
    }
};
